/**
 * Tender Summary → clipboard serializer.
 *
 * Flattens a {@link TenderOverview} into a plain-text and a minimal-HTML
 * representation for copy-to-clipboard. Citation markers (`[n]`) are replaced
 * with a human-readable `[<documentName>, p.<page>]` label so the pasted text
 * carries its provenance. The HTML transform is a deliberately small,
 * regex-based markdown converter — headings/bold/lists/paragraphs only — so no
 * new dependency is pulled in for a one-shot clipboard payload.
 *
 * Spec: Specs/tender-summary-spec.md
 */

import {
  KEY_FACT_FIELDS,
  KEY_FACT_LABELS,
  SECTION_LABELS,
  TENDER_OVERVIEW_SECTION_KEYS,
} from '@/types/tender-overview'
import type { ResolvedCitation, TenderOverview } from '@/types/tender-overview'
import { displayCitationPage } from '@/lib/citation-preview'

const SUMMARY_TITLE = 'Tender Summary'
const KEY_FACTS_HEADING = 'Key facts'
const NOT_STATED = 'Not stated'

// ===================
// Citations
// ===================

/** `[<documentName>, p.<page>]`, or `[<documentName>]` when the page is unknown. */
function citationLabel(citation: ResolvedCitation): string {
  const page = displayCitationPage(citation.page)
  return page != null
    ? `[${citation.documentName}, p.${page}]`
    : `[${citation.documentName}]`
}

/**
 * Replace `[n]` markers with a readable citation label. Matches on the
 * citation's `marker`, falling back to positional (1-based) lookup. An
 * unresolved marker is left verbatim.
 */
function replaceMarkers(md: string, citations: ResolvedCitation[]): string {
  return md.replace(/\[(\d+)\]/g, (match, digits) => {
    const n = Number(digits)
    const citation = citations.find((c) => c.marker === n) ?? citations[n - 1]
    return citation ? citationLabel(citation) : match
  })
}

// ===================
// Plain text
// ===================

function buildPlain(overview: TenderOverview): string {
  const lines: string[] = [SUMMARY_TITLE, '', KEY_FACTS_HEADING]

  for (const field of KEY_FACT_FIELDS) {
    const value = overview.keyFacts[field]?.value ?? NOT_STATED
    lines.push(`${KEY_FACT_LABELS[field]}: ${value}`)
  }

  for (const key of TENDER_OVERVIEW_SECTION_KEYS) {
    const section = overview.sections.find((s) => s.key === key)
    if (!section) continue
    lines.push('', SECTION_LABELS[key], replaceMarkers(section.contentMd, section.citations).trim())
  }

  return lines.join('\n')
}

// ===================
// Minimal markdown → HTML
// ===================

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/** Escape, then apply inline emphasis (`**bold**`). */
function inlineMd(text: string): string {
  return escapeHtml(text).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
}

/**
 * Block-level markdown → HTML: paragraphs, unordered lists, and inline bold.
 * Blank lines separate blocks and close open lists.
 */
function markdownToHtml(md: string): string {
  const out: string[] = []
  let listItems: string[] = []

  const flushList = () => {
    if (listItems.length > 0) {
      out.push(`<ul>${listItems.map((li) => `<li>${li}</li>`).join('')}</ul>`)
      listItems = []
    }
  }

  for (const raw of md.split('\n')) {
    const line = raw.trim()
    if (!line) {
      flushList()
      continue
    }
    const listMatch = line.match(/^[-*]\s+(.*)$/)
    if (listMatch) {
      listItems.push(inlineMd(listMatch[1]!))
      continue
    }
    flushList()
    out.push(`<p>${inlineMd(line)}</p>`)
  }

  flushList()
  return out.join('')
}

function buildHtml(overview: TenderOverview): string {
  const parts: string[] = [`<h2>${SUMMARY_TITLE}</h2>`, `<h3>${KEY_FACTS_HEADING}</h3>`]

  const facts = KEY_FACT_FIELDS.map((field) => {
    const value = overview.keyFacts[field]?.value ?? NOT_STATED
    return `<li><strong>${escapeHtml(KEY_FACT_LABELS[field])}:</strong> ${escapeHtml(value)}</li>`
  })
  parts.push(`<ul>${facts.join('')}</ul>`)

  for (const key of TENDER_OVERVIEW_SECTION_KEYS) {
    const section = overview.sections.find((s) => s.key === key)
    if (!section) continue
    parts.push(`<h3>${escapeHtml(SECTION_LABELS[key])}</h3>`)
    parts.push(markdownToHtml(replaceMarkers(section.contentMd, section.citations)))
  }

  return parts.join('')
}

// ===================
// Public API
// ===================

export interface OverviewClipboard {
  plain: string
  html: string
}

/** Serialize a tender overview into `{ plain, html }` clipboard payloads. */
export function buildOverviewClipboard(overview: TenderOverview): OverviewClipboard {
  return { plain: buildPlain(overview), html: buildHtml(overview) }
}
